import React from "react";
import { Box } from "@salestrak/ui";
import { USERNAME_MAX_LENGTH, usernameProblem } from "@salestrak/utilities";

import {
  DangerZoneGroup,
  type DangerZoneConfig,
} from "#app/components/settings/danger-zone";
import { EditableField } from "#app/components/settings/editable-field";
import { ProfileAvatar } from "#app/components/settings/profile-avatar";
import { SettingsGroup, SettingsGroupRow } from "#app/components/settings/settings-group";
import { JobTitleSelector } from "#app/components/entity-selectors";
import { TextValueCell } from "#app/components/fields/text-value-cell";
import { useAppData } from "#app/lib/data";
import { usePermission } from "#app/lib/hooks/use-permission";
import type { ProfileFormUser } from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";

// The API trims and enforces the same rules; checking here keeps a value it
// would refuse from being sent, and says why when the field is left.
const isValidName = (value: string) => value.trim().length > 0;
// Empty clears the username.
const isValidUsername = (value: string) =>
  !value.trim() || usernameProblem(value.trim()) === null;
const USERNAME_RULE = `One word of letters, numbers, dots, hyphens or underscores, at most ${USERNAME_MAX_LENGTH} characters.`;

// One control column for the whole group. An input left to itself takes the
// browser's intrinsic field width, which no button can be told to match, so
// the width is stated once here and every control in the group is given it —
// the Title select then lines up with the fields above it exactly.
const FIELD_WIDTH = 180;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// The prop type is the `ProfileForm_user` fragment (graphql/members.graphql) —
// this form declares the fields it renders and both screens that mount it
// spread them (R1). `email` is nullable on User, and the form renders it so.
export type { ProfileFormUser };

type ProfileFieldsGroupProps = Omittable<{
  user: ProfileFormUser;
  /** `value` is null exactly once: the Title row clearing the job title. */
  onFieldSave: (fieldId: string, value: string | null) => void;
  /** Group heading — "General" on a dedicated member page, "Profile" when the
   *  group sits among other groups (the merged preferences page). */
  label?: string;
}>;

type ProfileFormProps = Omittable<{
  user: ProfileFormUser;
  onFieldSave: (fieldId: string, value: string | null) => void;
  label?: string;
  dangerZone?: DangerZoneConfig[];
  /**
   * Extra settings groups rendered between General and the danger zone.
   * The self profile passes its Security group here; the admin member-edit
   * screen (which reuses this form for OTHER users) passes nothing.
   */
  children?: React.ReactNode;
}>;

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

export function ProfileFieldsGroup({
  user,
  onFieldSave,
  label = "General",
}: ProfileFieldsGroupProps) {
  // The Title row is the same job-title picker the profile island renders —
  // the shared workspace vocabulary, not a free-text input. Manage rights on
  // custom titles follow the picker's own rule: creator or manage.users.
  const { currentUser } = useAppData();
  const canManageTitles = usePermission("manage.users");
  // The admin member page renders this group for another member, so the
  // copy speaks to the viewer only on their own profile.
  const isSelf = currentUser?.userId === user.userId;

  return (
    <SettingsGroup label={label}>
      <SettingsGroupRow label="Avatar" subtitle="Recommended 256x256">
        <Box direction="row" justify="end">
          <ProfileAvatar user={user} />
        </Box>
      </SettingsGroupRow>

      <SettingsGroupRow label="Name">
        <EditableField
          errorMessage="Name is required."
          fieldId="name"
          label="Name"
          initialValue={user.name ?? ""}
          onSave={onFieldSave}
          validate={isValidName}
          width={FIELD_WIDTH}
        />
      </SettingsGroupRow>

      <SettingsGroupRow label="Email">
        <EditableField
          fieldId="email"
          label="Email"
          initialValue={user.email ?? ""}
          disabled
          onSave={onFieldSave}
          width={FIELD_WIDTH}
        />
      </SettingsGroupRow>

      <SettingsGroupRow label="Phone">
        {/* The one phone cell every phone property uses, framed as a form
            field: unset reads "—" like the Title select below (the row label
            already says Phone, so a flag and an example number at rest would
            only restate it), the click opens the country picker + as-you-type
            input in place, and a set number carries the call/copy/clear hover
            actions. E.164 in and out; a number that does not fit its region
            is refused and stays in the input to be fixed. */}
        <Box width={FIELD_WIDTH}>
          <TextValueCell
            emptyTextVariant="muted"
            inset="field"
            label="Phone"
            onCommit={(next) => {
              const text = next ?? "";
              if (text === (user.phoneNumber ?? "")) return;
              // The cell refuses a number that does not parse and keeps
              // the typed digits open for the user to fix.
              onFieldSave("phoneNumber", text);
            }}
            type="phone"
            value={user.phoneNumber}
          />
        </Box>
      </SettingsGroupRow>

      <SettingsGroupRow label="Title" subtitle={isSelf ? "Your job title" : "Job title"}>
        <Box width={FIELD_WIDTH}>
          <JobTitleSelector
            value={user.title ?? null}
            onValueChange={(title) => onFieldSave("title", title)}
            currentUserId={currentUser?.userId ?? null}
            canManage={canManageTitles}
            // A settings row wears the field chrome, so the Title row reads as
            // the select it is beside the inputs above it; the profile island
            // keeps the picker's bare inline trigger.
            trigger="field"
          />
        </Box>
      </SettingsGroupRow>

      <SettingsGroupRow
        label="Username"
        subtitle="One word, like a nickname or first name"
      >
        <EditableField
          errorMessage={USERNAME_RULE}
          fieldId="username"
          label="Username"
          initialValue={user.username ?? ""}
          onSave={onFieldSave}
          validate={isValidUsername}
          width={FIELD_WIDTH}
        />
      </SettingsGroupRow>
    </SettingsGroup>
  );
}

export function ProfileForm({
  user,
  onFieldSave,
  label,
  dangerZone,
  children,
}: ProfileFormProps) {
  return (
    <>
      <ProfileFieldsGroup user={user} onFieldSave={onFieldSave} label={label} />
      {children}
      {dangerZone ? <DangerZoneGroup dangerZone={dangerZone} /> : null}
    </>
  );
}
